import React, { useState, useEffect, useCallback, useMemo, lazy } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import {
  Drawer,
  Col,
  Row,
  Card,
  Pagination,
} from "antd";
import { Layout } from "@/common/layout";
import DashboardTable from "@/pages/dashboard/dashboard.table";
import DocumentFiltersContainer from "@/common/document-filters/document-filters-component";
import {
  setCurrentDashboardName as setCurrentDashboardNameAction,
  setCurrentPage as setCurrentPageAction,
  setValue,
  selectCurrentDashboardFields,
  updateDocumentCounts,
  setLargeTableView as setLargeTableViewAction,
  setThumbnailTableView as setThumbnailTableViewAction,
} from "@/pages/dashboard/dashboard.slice";
import {
  dashboardApi,
  useGetTableDataQuery,
} from "@/pages/dashboard/dashboard.reducer";
import { setFilter as setFilterAction } from "@/pages/shared/shared.reducer";
import PanelContainer from "@/common/panel";
import PanelLazyLoader from "@/common/panel-lazy-loader";
import DashboardTableActions from "@/common/document-filters/select-footer";
import SubTabButtons from "@/pages/dashboard/dashboard.main.subTabPanel";
import get from "lodash.get";
import panels from "@/constants/panels";
const UserProfile = lazy(() => import("@/common/user-profile/user-profile"));
import UnderChangeComment from "@/pages/dashboard/dashboard-panels/under-change-comment";
import DocumentCommentPanel from "@/pages/dashboard/dashboard-panels/dashboard.detail.comment";
import { DashboardTabs } from "@/common/dashboardTabs";
import { useNavigationItems } from "@/common/navigation/navigation.items";
import { FormProvider } from "@/common/form-context";
const DocumentDetailPage = lazy(() =>
  import("@/pages/dashboard/document-detail/document.detail.page")
);
import { getDashboardFilters } from "@/common/document-filters/document-filters-reducer";
const PreviewPdf = lazy(() => import("@/common/preview-pdf"));
import PageSizeSelector from "@/common/page-size-selector";

const NO_ITEMS = [];

const AllSitesDashboard = () => {
  const dispatch = useDispatch();
  const store = useStore();

  const selectedItems = useSelector((state) => get(state.shared, "dashboardTable.selectedItems")) || NO_ITEMS;
  const permissions = useSelector((state) => state.userDetails.permissions);
  const sharedFilters = useSelector((state) => get(state.shared, "dashboardTable.filters")) || NO_ITEMS;

  const setCurrentDashboardName = useCallback(() => dispatch(setCurrentDashboardNameAction("allSitesDashboard")), [dispatch]);
  const setFilter = useCallback((name, value, table = "dashboardTable") => dispatch(setFilterAction(table, name, value)), [dispatch]);
  const setCurrentPage = useCallback((value) => dispatch(setCurrentPageAction("dashboardTable", value)), [dispatch]);
  const setFilterDrawerVisible = useCallback((value) => dispatch(setValue({ key: "filterDrawerOpen", value })), [dispatch]);
  const setLargeTableView = useCallback(() => {
    dispatch(setFilterAction("dashboardTable", "includeThumbnail", false));
    dispatch(setLargeTableViewAction("allSitesDashboard"));
  }, [dispatch]);
  const setThumbnailTableView = useCallback(() => {
    dispatch(setFilterAction("dashboardTable", "includeThumbnail", true));
    dispatch(setThumbnailTableViewAction("allSitesDashboard"));
  }, [dispatch]);

  const { 
    sideView, 
    isThumbnailView, 
  } = useSelector(selectCurrentDashboardFields);

  const pdfPreviewDocNum = useSelector(
    (state) => state.dashboardPage[panels.pdfPreview]?.item.documentNumber || ""
  );
  const filterDrawerOpen = useSelector(
    (state) => state.dashboardPage.filterDrawerOpen
  );
  const item = useSelector((state) => state.dashboardPage.item);
  const pageSize = useSelector((state) => state.shared.dashboardTable.filters.pageSize);
  const currentPage = useSelector((state) => state.shared.dashboardTable.filters.currentPage);

  const [width, setWidth] = useState(1920);
  const [mobileView, setMobileView] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(true);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [ddvSearch, setDdvSearch] = useState(
    () => store.getState().shared?.dashboardTable?.filters?.documentNumber || ""
  );
  const [descriptionSearch, setDescriptionSearch] = useState(
    () => store.getState().shared?.dashboardTable?.filters?.keywords || ""
  );

  const combinedFilters = useMemo(
    () => ({ ...sharedFilters, pageSize, currentPage, siteId: null }),
    [sharedFilters, pageSize, currentPage]
  );

  const {
    data: tableData,
    refetch,
    isLoading,
  } = useGetTableDataQuery(combinedFilters, { refetchOnMountOrArgChange: true });

  const resultsCount = tableData?.totalItems || 0;
  const allItemsCount = tableData?.siteTotalItems || 0;

  const handleWindowResize = useCallback(() => setWidth(window.innerWidth), []);

  useEffect(() => {
    setFilter("siteId", null);
  }, [setFilter]);

  const updateTotalDocumentCount = useCallback(() => {
    dispatch(dashboardApi.endpoints.getDocumentsCount.initiate(0))
      .unwrap()
      .then((response) => {
        if (response) {
          dispatch(updateDocumentCounts({ allItems: response }));
        }
      })
      .catch((error) => {
        console.error("Error fetching document counts:", error);
      });
  }, [dispatch]);

  const handleSearch = useCallback(
    (newDdvSearch = "", newDescriptionSearch = "") => {
      setFilter("documentNumber", newDdvSearch);
      setFilter("vendorNumber", newDdvSearch);
      setFilter("keywords", newDescriptionSearch);
  }, [setFilter]);
  
  const resetSearchValues = useCallback(() => {
    setDdvSearch("");
    setDescriptionSearch("");
    handleSearch("", "");
  }, [handleSearch, setDdvSearch, setDescriptionSearch]);

  const handleFilterDrawerVisible = useCallback(
    (value) => {
      setFilterDrawerVisible(value);
      setShowFilterDrawer(value);
    },
    [setFilterDrawerVisible, setShowFilterDrawer]
  );

  const getMeasuredTableHeight = useCallback(() => {
    const holder = document.querySelector(
      ".dashboard-native-scroll-panel .ant-table-tbody-virtual-holder"
    );
    const footer = document.querySelector(".dashboard-table-footer");

    if (!holder || !footer) {
      return null;
    }

    const availableHeight =
      footer.getBoundingClientRect().top - holder.getBoundingClientRect().top;

    return availableHeight > 0 ? Math.ceil(availableHeight) : null;
  }, []);

  const getTableHeight = useCallback(() => {
    const measuredHeight = getMeasuredTableHeight();
    if (measuredHeight != null) {
      return measuredHeight;
    }

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (windowWidth >= 1912) {
      return windowHeight - 312;
    } else if (windowWidth >= 1440 && windowWidth < 1912) {
      return windowHeight - 375;
    } else if (windowWidth >= 1024 && windowWidth < 1440) {
      return windowHeight - 390;
    } else if (windowWidth >= 768 && windowWidth < 1023) {
      return windowHeight - 390;
    } else {
      return windowHeight - 405;
    }
  }, [getMeasuredTableHeight, sideView, showFilterPanel, width]);

  const getTopViewDashboardTableHeight = useCallback(() => {
    const measuredHeight = getMeasuredTableHeight();
    if (measuredHeight != null) {
      return measuredHeight;
    }

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (windowWidth >= 1912) {
      return windowHeight - 600;
    } else {
      return windowHeight - 650;
    } 
  }, [getMeasuredTableHeight, sideView, showFilterPanel, width]);

  const navItems = useNavigationItems();
  const dashboardItems = navItems.find((item) => item.title === "Dashboards");

  useEffect(() => {
    const adjustLayoutForWidth = () => {
      if (width <= 1440) {
        setShowFilterDrawer(true);
        setShowFilterPanel(false);
      } else {
        setShowFilterDrawer(false);
        setShowFilterPanel(true);
      }
      setMobileView(width < 768);
    };

    const updateDimensions = () => {
      handleWindowResize();
      adjustLayoutForWidth();
    };

    window.addEventListener("resize", updateDimensions);
    updateDimensions();

    return () => window.removeEventListener("resize", updateDimensions);
  }, [width, handleWindowResize]);

  useEffect(() => {
    document.title = "All Sites Dashboard";
    // Set the current dashboard name to allSitesDashboard
    setCurrentDashboardName();

    // Trigger document count update after dashboard name is set
    updateTotalDocumentCount();
  }, [setCurrentDashboardName, updateTotalDocumentCount]);

  const subTabsOptions = [
    {
      key: "1",
      label: "Results",
      count: resultsCount,
      icon: "ri-folder-3-line",
    },
  ];

  return (
    <Layout>
      <div className="dashboard-content dashboard-table-container">
        <Row gutter={[16, 16]}>
          <DashboardTabs
            style={{ width: "100%" }}
            dashboardItems={dashboardItems}
            showFilterPanel={showFilterPanel}
            setShowFilterPanel={setShowFilterPanel}
          >
            <Col
              span={sideView && showFilterPanel ? 19 : 24}
              className="dashboard-document-tab-content"
            >
              <div className={width <= 1440
                        ? "table-container" : sideView ? "table-container" : "top-view-table"}>
              {!sideView && showFilterPanel ? (
                <Row style={{
                        marginBottom: width <= 1440 ? "0px" : "16px",
                        border: "1px solid var(--dms-color-border-strong)",
                        borderRadius: "8px",
                      }}>
                  <Card className="top-filter-card">
                      <DocumentFiltersContainer
                        key="documentFilters"
                        table="dashboardTable"
                        ignoreSite={true}
                        mobileView={mobileView}
                        showFilterDrawer={showFilterDrawer}
                        resetSearch={resetSearchValues}
                        handleSearch={handleSearch}
                        updateTotalDocumentCount={updateTotalDocumentCount}
                        ddvSearch={ddvSearch}
                        descriptionSearch={descriptionSearch}
                        setDdvSearch={setDdvSearch}
                        setDescriptionSearch={setDescriptionSearch}
                      />
                  </Card>
                </Row>
              ) : null}
              <Card style={{
                  overflow: "hidden",
                  height: width <= 1440
                      ? "100%"
                      : sideView || !showFilterPanel
                      ? "100%"
                      : "calc(100vh - 455px)",
                  display: "flex",
                  flexDirection: "column",
                }}
                className="dashboard-table-card"
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                  className={`dashboard-table-container dashboard-table-responsive ${
                    width <= 1440
                        ? "filters-side"
                        : sideView || !showFilterPanel
                        ? "filters-side"
                        : "filters-above"
                  }`}
                >
                  <SubTabButtons
                    activeTabKey={1}
                    tableCount={{ 1: resultsCount }}
                    totalCount={resultsCount}
                    siteTotalCount={allItemsCount}
                    searchText="Search document/drawing # or vendor #"
                    ddvSearch={ddvSearch}
                    descriptionSearch={descriptionSearch}
                    setDdvSearch={setDdvSearch}
                    setDescriptionSearch={setDescriptionSearch}
                    handleSearch={handleSearch}
                    width={width}
                    setWidth={setWidth}
                    handleFilterDrawerVisible={handleFilterDrawerVisible}
                    pageSize={pageSize}
                    mobileView={mobileView}
                    tableViewVisible={false}
                    subTabsOptions={subTabsOptions}
                    isThumbnailView={true}
                    isDocumentsTab={false}
                    allSites={true}
                    tableId="dashboardTable"
                  />
                  <div className="dashboard-table-body">
                      <div className="content-dashboard-panel dashboard-native-scroll-panel">
                      <Card className="dashboard-card-body">
                        <DashboardTable
                          id="dashboardTable"
                          tableData={tableData?.data}
                          tableCount={resultsCount}
                          allItemsCount={allItemsCount}
                          refetch={refetch}
                          isLoading={isLoading}
                          showTotalCount={true}
                          showSiteColumn={true}
                          pageSize={pageSize}
                          currentPage={currentPage}
                          getTableHeight={sideView || !showFilterPanel ? getTableHeight : getTopViewDashboardTableHeight}
                          isResizable={true}
                          isReorderable={false}
                          updateTotalDocumentCount={updateTotalDocumentCount}
                          width={width}
                          resetSearch={resetSearchValues}
                        />
                      </Card>
                    </div>
                  </div>
                  <div className="dashboard-table-footer">
                    <Card className="dashboard-card-footer">
                      <Col className="footer-col-actions">
                        <DashboardTableActions
                          width={width}
                          className="col-6"
                          showMoreActions={false}
                          showAddToCart={false}
                          showAddToFavourites={false}
                          showRemoveFromCart={false}
                          showRemoveFromFavourites={false}
                          updateTotalDocumentCount={
                            updateTotalDocumentCount
                          }
                          dashboardStatusId={
                            sharedFilters.documentStatusId || 1
                          }
                          refetch={() => refetch()}
                        />
                      </Col>
                      <Col className="footer-col-pagination">
                        {width > 765 &&
                          <PageSizeSelector
                            tableId={"dashboardTable"}
                            tableDataCount={resultsCount}
                          />
                        } 
                        <Pagination
                          simple
                          current={currentPage}
                          pageSize={pageSize}
                          showSizeChanger={false}
                          showQuickJumper={false}
                          total={resultsCount}
                          hideOnSinglePage={true}
                          onChange={(page) => setCurrentPage(page)}
                        />
                      </Col>
                    </Card>
                  </div>
                </div>
              </Card>
              </div>
            </Col>
            {sideView && showFilterPanel && (
              <Col span={5} className="dashboard-document-tab-content">
                <Card style={{ height: "100%" }}>
                  <div className="sider-dashboard-panel">
                    <DocumentFiltersContainer
                      key="documentFilters"
                      table="dashboardTable"
                      ignoreSite={true}
                      mobileView={mobileView}
                      showFilterDrawer={showFilterDrawer}
                      resetSearch={resetSearchValues}
                      handleSearch={handleSearch}
                      closeFilterDrawer={() => handleFilterDrawerVisible(false)}
                      updateTotalDocumentCount={updateTotalDocumentCount}
                      setLargeTableView={setLargeTableView}
                      setThumbnailTableView={setThumbnailTableView}
                      ddvSearch={ddvSearch}
                      descriptionSearch={descriptionSearch}
                      setDdvSearch={setDdvSearch}
                      setDescriptionSearch={setDescriptionSearch}
                    />
                  </div>
                </Card>
              </Col>
            )}
            <Drawer
              placement={sideView ? "right" : "top"}
              closable={false}
              className="document-filters-drawer"
              styles={{
                header: { padding: 0 },
                body: {
                  padding: 0,
                  backgroundColor: "var(--dms-color-bg-muted)",
                  height: "100%",
                },
              }}
              width={360}
              onClose={() => handleFilterDrawerVisible(false)}
              open={filterDrawerOpen}
            >
              {showFilterDrawer && filterDrawerOpen && (
                <DocumentFiltersContainer
                  key="documentFilters"
                  table="dashboardTable"
                  ignoreSite={true}
                  mobileView={mobileView}
                  showFilterDrawer={showFilterDrawer}
                  resetSearch={resetSearchValues}
                  handleSearch={handleSearch}
                  closeFiltersDrawer={() =>
                    handleFilterDrawerVisible(false)
                  }
                  isThumbnailView={isThumbnailView}
                  tableViewVisible={true}
                  setLargeTableView={setLargeTableView}
                  setThumbnailTableView={setThumbnailTableView}
                  width={width}
                  ddvSearch={ddvSearch}
                  descriptionSearch={descriptionSearch}
                  setDdvSearch={setDdvSearch}
                  setDescriptionSearch={setDescriptionSearch}
                />
              )}
            </Drawer>
          </DashboardTabs>
        </Row>
      </div>
      <FormProvider>
        <PanelContainer
          id={panels.pdfPreview}
          title={`Document Preview: ${pdfPreviewDocNum.toUpperCase()}`}
          className="panel preview-pdf-panel"
          content={<PanelLazyLoader component={PreviewPdf} />}
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.dashboard}
          title={`Document Detail: ${item.documentNumber?.toUpperCase()}`}
          className="panel document-detail"
          content={
            <PanelLazyLoader
              component={DocumentDetailPage}
              refetch={() => {
                refetch();
                dispatch(getDashboardFilters(combinedFilters, "dashboardTable", true));
              }}
              tableId="dashboardTable"
              updateTotalDocumentCount={updateTotalDocumentCount}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.systemUser}
          title="View User Details"
          footer={null}
          content={
            <PanelLazyLoader
              component={UserProfile} 
              panelId={panels.systemUser} 
              readonly={true} 
            />
        }
          className={"panel"}
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.showChangeComment}
          title={`Show change comment`}
          zIndex={3}
          content={
            <PanelLazyLoader
              component={DocumentCommentPanel}
              refetch={refetch}
              permissions={permissions}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.addChangeComment}
          title="Add Under Change Comment"
          footer={null}
          content={
            <PanelLazyLoader
              component={UnderChangeComment}
              tableId="dashboardTable"
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      {/* <FormProvider>
                <PanelContainer
                    id={panels.jobCreate}
                    title="Create new job"
                    footer={null}
                    content={<CreateJobSummary fullView={true} tableId={['dashboardTable']} documents={selectedItems} />}
                />
            </FormProvider> */}
    </Layout>
  );
};

export default AllSitesDashboard;
